import { lazy, LazyExoticComponent, ComponentType } from "react";

const isChunkLoadError = (e: unknown): boolean => e instanceof Error
  && (e.name === "ChunkLoadError" || /Loading chunk \d+ failed/i.test((e as Error).message));

/**
 * Wraps React.lazy() to retry the dynamic import on ChunkLoadError (e.g. network blip or stale cache).
 * After retries are exhausted, the error is rethrown so an Error Boundary can show a Retry UI.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 2,
  delayMs = 1000,
): LazyExoticComponent<T> {
  const load = (attempt: number): Promise<{ default: T }> => {
    return importFn().catch((e: unknown) => {
      if (isChunkLoadError(e) && attempt < retries) {
        return new Promise<{ default: T }>((resolve, reject) => {
          setTimeout(() => load(attempt + 1).then(resolve).catch(reject), delayMs);
        });
      }
      throw e;
    });
  };
  return lazy(() => load(0));
}
